import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bot, ChevronDown, ChevronRight, Sparkles, Wrench } from 'lucide-react';
import type { AiAnalysis, AiSubjectType } from '@itr/shared';
import { formatRelative } from '@itr/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { Badge, useFetch, useToast } from './ui';

/**
 * AI root-cause panel.
 *
 * Shown on alerts and tickets. The tool calls are deliberately visible rather
 * than hidden behind the conclusion: an unsourced diagnosis is a guess with
 * better grammar, and an engineer needs to be able to check the reasoning
 * against what was actually observed before acting on it.
 */
export function AiAnalysisPanel({ subjectType, subjectId }: { subjectType: AiSubjectType; subjectId: string }) {
  const toast = useToast();
  const { can } = useAuth();
  const [running, setRunning] = useState(false);
  const [hint, setHint] = useState('');
  const [showHint, setShowHint] = useState(false);

  const status = useFetch(
    () => api.get<{ configured: boolean; provider: 'anthropic' | 'ollama'; model: string }>('/api/ai/status'),
    [],
  );
  const analyses = useFetch(
    () => api.get<AiAnalysis[]>('/api/ai/analyses', { subjectType, subjectId, limit: 3 }),
    [subjectType, subjectId],
  );

  const latest = analyses.data?.[0];

  const analyse = async () => {
    setRunning(true);
    try {
      await api.post('/api/ai/analyse', { subjectType, subjectId, hint: hint.trim() || undefined });
      analyses.reload();
      setHint('');
      setShowHint(false);
    } catch (err) {
      toast('Analysis failed', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setRunning(false);
    }
  };

  if (status.data && !status.data.configured) {
    return (
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-header">
          <Bot size={14} />
          <span className="card-title">AI analysis</span>
        </div>
        <div className="card-body">
          <p className="subtle" style={{ fontSize: 13, lineHeight: 1.55 }}>
            Not configured. {can('settings:manage') ? (
              <>
                Set it up under <Link to="/settings">Settings → AI assistance</Link>
              </>
            ) : (
              'Ask an administrator to set it up under Settings → AI assistance'
            )}{' '}
            to let the model investigate an incident — it reads the metric history, health transitions, sibling
            alerts and cached MikroTik config, runs live ping, traceroute and port checks, then reports a root cause
            with the evidence behind it.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="card-header">
        <Bot size={14} style={{ color: 'var(--itr-primary)' }} />
        <span className="card-title">AI analysis</span>
        <span className="grow" />
        {latest && <span className="card-subtitle">{formatRelative(latest.startedAt)}</span>}
        <button className="btn btn-sm" onClick={() => setShowHint((v) => !v)} disabled={running}>
          Add context
        </button>
        <button className="btn btn-sm btn-primary" onClick={analyse} disabled={running}>
          {running ? <span className="spinner" style={{ width: 13, height: 13 }} /> : <Sparkles size={13} />}
          {running ? 'Investigating…' : latest ? 'Re-analyse' : 'Diagnose'}
        </button>
      </div>

      {showHint && (
        <div className="card-body" style={{ borderBottom: 'var(--itr-border-w) solid var(--itr-border)' }}>
          <input
            className="input"
            placeholder="Anything Claude cannot see — e.g. 'started after the firewall upgrade last night'"
            value={hint}
            onChange={(e) => setHint(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !running) void analyse();
            }}
            autoFocus
          />
        </div>
      )}

      <div className="card-body">
        {running && !latest ? (
          <div className="row" style={{ gap: 9, fontSize: 13, color: 'var(--itr-text-muted)' }}>
            <span className="spinner" />
            Reading metrics and running live checks — this usually takes 20–45 seconds.
          </div>
        ) : !latest ? (
          <p className="subtle" style={{ fontSize: 13, lineHeight: 1.55 }}>
            Claude will pull the metric history around the failure, check whether the device is reachable now, and look
            at what else on the estate is unhealthy before concluding.
          </p>
        ) : (
          <AnalysisResult analysis={latest} running={running} />
        )}
      </div>
    </div>
  );
}

function AnalysisResult({ analysis, running }: { analysis: AiAnalysis; running: boolean }) {
  const [showTools, setShowTools] = useState(false);

  if (analysis.status === 'failed') {
    return (
      <div style={{ fontSize: 13, color: 'var(--itr-critical)' }}>
        {analysis.error ?? 'The analysis failed.'}
      </div>
    );
  }

  if (analysis.status === 'running' || running) {
    return (
      <div className="row" style={{ gap: 9, fontSize: 13, color: 'var(--itr-text-muted)' }}>
        <span className="spinner" /> Investigating…
      </div>
    );
  }

  const confidenceIntent = analysis.confidence === 'high' ? 'good' : analysis.confidence === 'medium' ? 'warn' : 'bad';

  return (
    <div className="col" style={{ gap: 14 }}>
      <div>
        <div className="row" style={{ gap: 8, marginBottom: 6 }}>
          <Badge intent={confidenceIntent}>{analysis.confidence ?? 'unknown'} confidence</Badge>
          <span className="grow" />
          <span className="subtle" style={{ fontSize: 11 }}>
            {analysis.model}
            {analysis.inputTokens !== null && ` · ${analysis.inputTokens + (analysis.outputTokens ?? 0)} tokens`}
          </span>
        </div>
        <div style={{ fontSize: 14.5, fontWeight: 560, lineHeight: 1.5 }}>{analysis.summary}</div>
      </div>

      {analysis.rootCause && (
        <div>
          <SectionLabel>Root cause</SectionLabel>
          <div style={{ fontSize: 13.5, lineHeight: 1.6 }}>{analysis.rootCause}</div>
        </div>
      )}

      {analysis.evidence.length > 0 && (
        <div>
          <SectionLabel>Evidence</SectionLabel>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.65 }}>
            {analysis.evidence.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
      )}

      {analysis.remediation.length > 0 && (
        <div>
          <SectionLabel>Suggested next steps</SectionLabel>
          <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.65 }}>
            {analysis.remediation.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ol>
        </div>
      )}

      {analysis.toolCalls.length > 0 && (
        <div>
          <button
            className="row"
            onClick={() => setShowTools((v) => !v)}
            style={{ gap: 5, background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--itr-text-subtle)', fontSize: 12 }}
          >
            {showTools ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            <Wrench size={12} />
            {analysis.toolCalls.length} check{analysis.toolCalls.length === 1 ? '' : 's'} run
          </button>

          {showTools && (
            <div className="col" style={{ gap: 6, marginTop: 8 }}>
              {analysis.toolCalls.map((call, i) => (
                <div
                  key={i}
                  style={{
                    padding: '8px 10px',
                    borderRadius: 'var(--itr-radius-sm)',
                    background: 'var(--itr-surface-sunken)',
                    fontSize: 11.5,
                  }}
                >
                  <div className="row" style={{ gap: 8 }}>
                    <span className="mono" style={{ fontWeight: 600 }}>
                      {call.name}
                    </span>
                    <span className="subtle mono truncate grow">{JSON.stringify(call.input)}</span>
                    <span className="subtle nums">{call.ms} ms</span>
                  </div>
                  <div className="mono subtle" style={{ marginTop: 4, wordBreak: 'break-all', lineHeight: 1.5 }}>
                    {call.resultPreview}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="subtle"
      style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: 5 }}
    >
      {children}
    </div>
  );
}
